/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * GridSubItems — grid overlay above the bar.
 *
 * Small grid pops up above the bottom bar showing sub-items in a grid layout.
 * Each item has a small star button for one-tap favoriting.
 */

import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLayoutStore, useFavorites, useFavoritesDisplayStyle, FAVORITES_MAX } from '@/stores/layoutStore';
import type { BottomNavItem } from './types';

interface Props {
  items: BottomNavItem[];
  onNavigate: () => void;
}

export function GridSubItems({ items, onNavigate }: Props) {
  const { t } = useTranslation('common');
  const favorites = useFavorites();
  const displayStyle = useFavoritesDisplayStyle();
  const addFavorite = useLayoutStore((s) => s.addFavorite);
  const removeFavorite = useLayoutStore((s) => s.removeFavorite);
  const isFull = favorites.length >= FAVORITES_MAX[displayStyle];

  return (
    <div className="bn-grid-overlay">
      <div className="bn-grid">
        {items.map((item) => {
          const isFav = favorites.includes(item.href);
          return (
            <div key={item.href} className="bn-grid-cell">
              <NavLink
                to={item.href}
                end
                className={({ isActive }) => cn('bn-grid-item', isActive && 'bn-grid-active')}
                onClick={onNavigate}
              >
                <div className="bn-grid-icon-wrap">
                  <item.icon size={22} />
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className={cn('bn-grid-badge', item.badgeVariant === 'danger' && 'danger')}>
                      {item.badge}
                    </span>
                  )}
                </div>
                <span className="bn-grid-label">{t(item.labelKey)}</span>
              </NavLink>
              <button
                className={cn('bn-grid-star', isFav && 'bn-grid-star--active')}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isFav) removeFavorite(item.href);
                  else if (!isFull) addFavorite(item.href);
                }}
                disabled={!isFav && isFull}
                type="button"
                aria-label={isFav ? t('favorites.remove') : t('favorites.add')}
              >
                <Star size={11} fill={isFav ? 'currentColor' : 'none'} />
              </button>
            </div>
          );
        })}
      </div>

      <style>{`
        .bn-grid-cell {
          position: relative;
        }
        .bn-grid-star {
          position: absolute;
          top: 4px;
          right: 4px;
          width: 22px;
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          border-radius: 50%;
          background: transparent;
          color: var(--color-text-muted);
          cursor: pointer;
          opacity: 0.5;
          transition: color 0.15s, opacity 0.15s, transform 0.15s;
          z-index: 2;
        }
        .bn-grid-star:hover:not(:disabled),
        .bn-grid-star--active {
          color: var(--color-warning);
          opacity: 1;
        }
        .bn-grid-star--active {
          transform: scale(1.1);
        }
        .bn-grid-star:disabled {
          opacity: 0.15;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}
